import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Pressable,
  ActivityIndicator,
  Image,
  TextInput,
  Linking,
} from 'react-native';
import WebScrollView from '../components/WebScrollView';
import { shadows } from '../lib/styles';
import {
  Building,
  Globe,
  Mail,
  MessageSquare,
  Phone,
  UserCheck,
  Users,
} from '../lib/icons';
import { supabase } from '../lib/supabase';
import { useActiveHome } from '../contexts/ActiveHomeContext';
import {
  linkReferringAgent,
  validateAgentIdentifier,
  type ReferringAgent,
} from '../lib/referringAgent';

interface ManagerProfile {
  id: string;
  full_name: string | null;
  profile_photo_url: string | null;
}

interface AgentRecord {
  id: string;
  full_name: string;
  brokerage: string | null;
  email: string | null;
  phone_number: string | null;
  photo_url: string | null;
  website_url: string | null;
  bio: string | null;
  slug: string;
}

const sanitizePhoneForLink = (raw: string): string => raw.replace(/[^\d+]/g, '');

const Team = () => {
  const navigate = useNavigate();
  const { activeHome, loading: activeHomeLoading } = useActiveHome();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [manager, setManager] = useState<ManagerProfile | null>(null);
  const [managerConversationId, setManagerConversationId] = useState<string | null>(null);

  const [agent, setAgent] = useState<AgentRecord | null>(null);

  const [showLink, setShowLink] = useState(false);
  const [linkInput, setLinkInput] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  const homeManagerLabel = activeHome?.home_manager || null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) {
          if (!cancelled) {
            setManager(null);
            setAgent(null);
          }
          return;
        }

        // Profile -> referring_agent_id (joined to the agent record).
        const { data: profileRow, error: profileErr } = await supabase
          .from('profiles')
          .select(
            `referring_agent_id,
             agent:real_estate_agents!profiles_referring_agent_id_fkey (
               id, full_name, brokerage, email, phone_number,
               photo_url, website_url, bio, slug
             )`
          )
          .eq('id', user.id)
          .single();
        if (profileErr) throw profileErr;

        if (!cancelled) {
          const agentRow = (profileRow as any)?.agent as AgentRecord | null;
          setAgent(agentRow || null);
        }

        // Resolve the home manager profile via the home-manager conversation
        // for the active home (preferred path - matches messaging).
        if (activeHome?.id) {
          const { data: convo } = await supabase
            .from('conversations')
            .select(
              `id,
               home_manager:profiles!conversations_home_manager_id_fkey (
                 id, full_name, profile_photo_url
               )`
            )
            .eq('homeowner_id', user.id)
            .eq('type', 'home_manager')
            .eq('home_id', activeHome.id)
            .maybeSingle();
          if (!cancelled) {
            setManagerConversationId(convo?.id || null);
            const m = (convo as any)?.home_manager as ManagerProfile | null;
            setManager(m || null);
          }
        } else {
          if (!cancelled) {
            setManagerConversationId(null);
            setManager(null);
          }
        }
      } catch (err: any) {
        console.error('Error loading team:', err);
        if (!cancelled) setError(err?.message || 'Failed to load your team');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeHome?.id]);

  const handleMessageManager = async () => {
    if (managerConversationId) {
      navigate(`/conversations/${managerConversationId}`);
      return;
    }
    // Fallback: send the user to the conversations list, which auto-creates
    // a home-manager conversation for any home that's missing one.
    navigate('/conversations');
  };

  const handleCall = (phone: string) => {
    if (Platform.OS === 'web') {
      window.location.href = `tel:${sanitizePhoneForLink(phone)}`;
    } else {
      Linking.openURL(`tel:${sanitizePhoneForLink(phone)}`).catch(() => {});
    }
  };

  const handleText = (phone: string) => {
    if (Platform.OS === 'web') {
      window.location.href = `sms:${sanitizePhoneForLink(phone)}`;
    } else {
      Linking.openURL(`sms:${sanitizePhoneForLink(phone)}`).catch(() => {});
    }
  };

  const handleEmail = (email: string, agentName: string) => {
    const subject = encodeURIComponent('Question from your HoneyDone homeowner');
    const body = encodeURIComponent(
      `Hi ${agentName.split(' ')[0]},\n\n`
    );
    const url = `mailto:${email}?subject=${subject}&body=${body}`;
    if (Platform.OS === 'web') {
      window.location.href = url;
    } else {
      Linking.openURL(url).catch(() => {});
    }
  };

  const handleWebsite = (url: string) => {
    const safe = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    if (Platform.OS === 'web') {
      window.open(safe, '_blank', 'noopener,noreferrer');
    } else {
      Linking.openURL(safe).catch(() => {});
    }
  };

  const handleLinkAgent = async () => {
    const identifier = linkInput.trim();
    if (!identifier) {
      setLinkError('Enter an agent code or link');
      return;
    }
    setLinking(true);
    setLinkError(null);
    try {
      // Pre-validate so we can show a friendly error before mutating.
      const check = await validateAgentIdentifier(identifier);
      if (!check.valid) {
        setLinkError(check.error || 'No matching agent found');
        return;
      }
      const result = await linkReferringAgent(identifier, 'self_linked');
      if (!result.success) {
        setLinkError(result.error);
        return;
      }
      // Refresh local state by re-reading the joined agent row.
      const { data: profileRow } = await supabase
        .from('profiles')
        .select(
          `agent:real_estate_agents!profiles_referring_agent_id_fkey (
             id, full_name, brokerage, email, phone_number,
             photo_url, website_url, bio, slug
           )`
        )
        .eq('id', (await supabase.auth.getUser()).data.user!.id)
        .single();
      setAgent(((profileRow as any)?.agent as AgentRecord) || null);
      setShowLink(false);
      setLinkInput('');
    } catch (err: any) {
      console.error('Error linking agent:', err);
      setLinkError(err?.message || 'Failed to link agent');
    } finally {
      setLinking(false);
    }
  };

  const isLoading = loading || activeHomeLoading;

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#000" />
      </View>
    );
  }

  return (
    <WebScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Your Team</Text>
        <Text style={styles.headerSubtitle}>
          The people working with you on your home.
        </Text>
      </View>

      {error ? (
        <View style={styles.errorBanner}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      ) : null}

      <ManagerCard
        manager={manager}
        managerLabel={homeManagerLabel}
        onMessage={handleMessageManager}
      />

      <AgentCard
        agent={agent}
        showLinker={showLink}
        onShowLinker={() => {
          setLinkError(null);
          setLinkInput('');
          setShowLink(true);
        }}
        onCancelLinker={() => {
          setShowLink(false);
          setLinkError(null);
          setLinkInput('');
        }}
        linkInput={linkInput}
        onLinkInputChange={setLinkInput}
        linking={linking}
        linkError={linkError}
        onLink={handleLinkAgent}
        onCall={handleCall}
        onText={handleText}
        onEmail={handleEmail}
        onWebsite={handleWebsite}
      />
    </WebScrollView>
  );
};

const ManagerCard = ({
  manager,
  managerLabel,
  onMessage,
}: {
  manager: ManagerProfile | null;
  managerLabel: string | null;
  onMessage: () => void;
}) => {
  const displayName =
    manager?.full_name || (managerLabel && managerLabel !== 'Unassigned' ? managerLabel : null);
  const initial = (displayName || '?').charAt(0).toUpperCase();
  const isUnassigned = !displayName;

  return (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <View style={styles.badge}>
          <Users size={12} color="#1d4ed8" />
          <Text style={styles.badgeText}>Home Manager</Text>
        </View>
      </View>

      <View style={styles.identityRow}>
        {manager?.profile_photo_url ? (
          <Image source={{ uri: manager.profile_photo_url }} style={styles.avatar} />
        ) : (
          <View style={[styles.avatar, styles.avatarFallback]}>
            <Text style={styles.avatarFallbackText}>{initial}</Text>
          </View>
        )}
        <View style={styles.identityCol}>
          <Text style={styles.identityName}>
            {displayName || 'No home manager assigned yet'}
          </Text>
          {!isUnassigned ? (
            <Text style={styles.identitySubtitle}>Your point of contact at HoneyDone</Text>
          ) : (
            <Text style={styles.identitySubtitle}>
              We'll assign your home manager shortly.
            </Text>
          )}
        </View>
      </View>

      <View style={styles.actionsRow}>
        <Pressable
          style={[styles.actionButton, styles.actionButtonPrimary]}
          onPress={onMessage}
        >
          <MessageSquare size={16} color="#ffffff" />
          <Text style={styles.actionButtonPrimaryText}>Send a message</Text>
        </Pressable>
      </View>
    </View>
  );
};

const AgentCard = ({
  agent,
  showLinker,
  onShowLinker,
  onCancelLinker,
  linkInput,
  onLinkInputChange,
  linking,
  linkError,
  onLink,
  onCall,
  onText,
  onEmail,
  onWebsite,
}: {
  agent: AgentRecord | null;
  showLinker: boolean;
  onShowLinker: () => void;
  onCancelLinker: () => void;
  linkInput: string;
  onLinkInputChange: (v: string) => void;
  linking: boolean;
  linkError: string | null;
  onLink: () => void;
  onCall: (phone: string) => void;
  onText: (phone: string) => void;
  onEmail: (email: string, name: string) => void;
  onWebsite: (url: string) => void;
}) => {
  if (!agent) {
    return (
      <View style={styles.card}>
        <View style={styles.cardHeader}>
          <View style={[styles.badge, styles.badgeAgent]}>
            <UserCheck size={12} color="#0f766e" />
            <Text style={[styles.badgeText, styles.badgeTextAgent]}>Real Estate Agent</Text>
          </View>
        </View>

        {!showLinker ? (
          <View style={styles.emptyAgent}>
            <Text style={styles.emptyAgentTitle}>
              Were you referred by a real estate agent?
            </Text>
            <Text style={styles.emptyAgentText}>
              Add their referral code or personal link and they'll appear here so you can
              stay in touch.
            </Text>
            <Pressable style={styles.emptyAgentButton} onPress={onShowLinker}>
              <Text style={styles.emptyAgentButtonText}>Link an agent</Text>
            </Pressable>
          </View>
        ) : (
          <View style={styles.linker}>
            <Text style={styles.linkerLabel}>Referral code or link</Text>
            <TextInput
              style={styles.input}
              value={linkInput}
              onChangeText={onLinkInputChange}
              placeholder="AGENT-ABC123 or jane-doe"
              placeholderTextColor="#9ca3af"
              autoCapitalize="characters"
              autoCorrect={false}
            />
            {linkError ? <Text style={styles.errorText}>{linkError}</Text> : null}
            <View style={styles.linkerActions}>
              <Pressable
                style={[styles.actionButton, styles.actionButtonPrimary, linking && styles.disabled]}
                onPress={onLink}
                disabled={linking}
              >
                <Text style={styles.actionButtonPrimaryText}>
                  {linking ? 'Linking…' : 'Link agent'}
                </Text>
              </Pressable>
              <Pressable style={styles.actionButton} onPress={onCancelLinker}>
                <Text style={styles.actionButtonText}>Cancel</Text>
              </Pressable>
            </View>
          </View>
        )}
      </View>
    );
  }

  const initial = agent.full_name.charAt(0).toUpperCase();
  return (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <View style={[styles.badge, styles.badgeAgent]}>
          <UserCheck size={12} color="#0f766e" />
          <Text style={[styles.badgeText, styles.badgeTextAgent]}>Real Estate Agent</Text>
        </View>
      </View>

      <View style={styles.identityRow}>
        {agent.photo_url ? (
          <Image source={{ uri: agent.photo_url }} style={styles.avatar} />
        ) : (
          <View style={[styles.avatar, styles.avatarFallback]}>
            <Text style={styles.avatarFallbackText}>{initial}</Text>
          </View>
        )}
        <View style={styles.identityCol}>
          <Text style={styles.identityName}>{agent.full_name}</Text>
          {agent.brokerage ? (
            <View style={styles.brokerageRow}>
              <Building size={13} color="#6b7280" />
              <Text style={styles.identitySubtitle}>{agent.brokerage}</Text>
            </View>
          ) : null}
        </View>
      </View>

      {agent.bio ? <Text style={styles.bio}>{agent.bio}</Text> : null}

      <View style={styles.actionsGrid}>
        {agent.phone_number ? (
          <>
            <Pressable
              style={[styles.actionButton, styles.actionButtonPrimary]}
              onPress={() => onCall(agent.phone_number!)}
            >
              <Phone size={16} color="#ffffff" />
              <Text style={styles.actionButtonPrimaryText}>Call</Text>
            </Pressable>
            <Pressable
              style={styles.actionButton}
              onPress={() => onText(agent.phone_number!)}
            >
              <MessageSquare size={16} color="#111827" />
              <Text style={styles.actionButtonText}>Text</Text>
            </Pressable>
          </>
        ) : null}
        {agent.email ? (
          <Pressable
            style={styles.actionButton}
            onPress={() => onEmail(agent.email!, agent.full_name)}
          >
            <Mail size={16} color="#111827" />
            <Text style={styles.actionButtonText}>Email</Text>
          </Pressable>
        ) : null}
        {agent.website_url ? (
          <Pressable
            style={styles.actionButton}
            onPress={() => onWebsite(agent.website_url!)}
          >
            <Globe size={16} color="#111827" />
            <Text style={styles.actionButtonText}>Website</Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    gap: 16,
    ...(Platform.OS === 'web' && {
      maxWidth: 720,
      width: '100%',
      marginHorizontal: 'auto' as any,
      paddingTop: 24,
      paddingBottom: 96,
    }),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200,
  },
  header: {
    gap: 4,
    marginBottom: 4,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#111827',
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#6b7280',
  },
  errorBanner: {
    backgroundColor: '#fee2e2',
    padding: 12,
    borderRadius: 8,
  },
  errorText: {
    color: '#dc2626',
    fontSize: 13,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 20,
    gap: 16,
    ...(Platform.OS === 'web' && shadows.sm),
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#dbeafe',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
  },
  badgeAgent: {
    backgroundColor: '#ccfbf1',
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#1d4ed8',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  badgeTextAgent: {
    color: '#0f766e',
  },
  identityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#e5e7eb',
  },
  avatarFallback: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarFallbackText: {
    fontSize: 22,
    fontWeight: '600',
    color: '#374151',
  },
  identityCol: {
    flex: 1,
    gap: 4,
  },
  identityName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  identitySubtitle: {
    fontSize: 13,
    color: '#6b7280',
  },
  brokerageRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  bio: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
  },
  actionsRow: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  actionsGrid: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    backgroundColor: '#ffffff',
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  actionButtonPrimary: {
    backgroundColor: '#000000',
    borderColor: '#000000',
  },
  actionButtonPrimaryText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  disabled: {
    opacity: 0.6,
  },
  emptyAgent: {
    gap: 8,
    alignItems: 'flex-start',
  },
  emptyAgentTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111827',
  },
  emptyAgentText: {
    fontSize: 13,
    color: '#6b7280',
    lineHeight: 19,
  },
  emptyAgentButton: {
    marginTop: 4,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: '#0f766e',
  },
  emptyAgentButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  linker: {
    gap: 8,
  },
  linkerLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  input: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: '#111827',
    backgroundColor: '#ffffff',
  },
  linkerActions: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
    marginTop: 4,
  },
});

export default Team;
